/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useRef, ReactElement, useEffect } from "react";
import IdleTimer from "react-idle-timer";
import { useAuthContext } from "../../../contexts/AuthProvider";
import Modal from "../../Modal";
import { Text, Heading } from "../../Type";
import styles from "./index.module.scss";

type PropsType = {
  children: ReactElement | ReactElement[];
};

const SESSION_TIMEOUT = parseInt(
  `${process.env.REACT_APP_LOGOUT_AFTER_IDLE_FOR}`,
  10
); // 60 mins for dev, 10 mins for production
const WARNING_TIMEOUT = 1000 * 5; // 1 min

// Note: The Continue button on the modal is functionally redundant.
// By interacting with the button (or even moving the mouse towards it),
// the idle timer will be implictly reset and the statement in the modal
// will no longer be true. We could hide the modal when the timer resets
// but that would be a confusing UX, so we leave the Continue button as
// a placebo.

const SessionTimer = ({ children }: PropsType) => {
  const { timeout } = useAuthContext();
  const [warningModalIsOpen, setWarningModalIsOpen] = useState(false);
  const idleTimerRef = useRef<IdleTimer | null>(null);

  // Show the warning when time is almost out
  useEffect(() => {
    const warningTimeout = Math.min(
      WARNING_TIMEOUT,
      Math.round(SESSION_TIMEOUT / 2)
    );
    const warningPoll = setInterval(() => {
      const timeRemaining =
        idleTimerRef.current?.getRemainingTime() || SESSION_TIMEOUT;
      if (!warningModalIsOpen && timeRemaining <= warningTimeout) {
        setWarningModalIsOpen(true);
      } else if (warningModalIsOpen && timeRemaining > warningTimeout) {
        setWarningModalIsOpen(false);
      }
    }, 1000);
    return () => clearInterval(warningPoll);
  }, [warningModalIsOpen]);

  const handleIdle = () => {
    timeout();
  };

  const handleClose = () => {
    setWarningModalIsOpen(false);
  };

  return (
    <IdleTimer
      ref={idleTimerRef as any}
      timeout={SESSION_TIMEOUT}
      onIdle={handleIdle}
      crossTab={{
        emitOnAllTabs: true,
        // https://github.com/SupremeTechnopriest/react-idle-timer/issues/183
        type: "localStorage",
      }}
    >
      {children}
      <Modal
        isOpen={warningModalIsOpen}
        title="Session Timeout"
        onClose={handleClose}
      >
        <div className={styles.content}>
          <Heading size="h4" className={styles.contentHeading}>
            Are you still there?
          </Heading>
          <Text>
            For added security, you will be logged out soon due to inactivity.
          </Text>
          <Text>Interact with the page in some way to stay logged in.</Text>
        </div>
      </Modal>
    </IdleTimer>
  );
};

export default SessionTimer;
